/** Title-cases plan tier slugs and display names (Enterprise, Starter, …). */
export function formatPlanLabel(value) {
  if (!value) {
    return 'Plan';
  }
  const trimmed = String(value).trim();
  if (!trimmed) {
    return 'Plan';
  }
  return trimmed
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
}

/** Human-readable label for an entitlement key (inst.courses → Courses). */
const ENTITLEMENT_LABEL_OVERRIDES = {
  scorm_training: 'SCORM / video training',
  api_access: 'API access',
  custom_ai_tutor_persona: 'Custom AI tutor persona',
  ai_course_builder: 'AI course builder',
  learning_mode_ai_tutor: 'Learning mode AI tutor',
  in_app_purchase_config: 'In-app purchase config',
};

export function formatEntitlementLabel(entKey) {
  if (!entKey) {
    return 'Setting';
  }
  const key = String(entKey);
  if (ENTITLEMENT_LABEL_OVERRIDES[key]) {
    return ENTITLEMENT_LABEL_OVERRIDES[key];
  }
  const stripped = key.replace(/^(inst|learner|lrn|plan)\./i, '');
  return formatPlanLabel(stripped.replace(/\./g, ' '));
}

/** Groups entitlements for the admin dialog sections. */
export function groupEntitlements(entitlements) {
  const list = Array.isArray(entitlements) ? entitlements : [];
  return {
    limits: list.filter((item) => item.kind === 'int'),
    features: list.filter((item) => item.kind === 'bool'),
    text: list.filter((item) => item.kind === 'text'),
    other: list.filter((item) => !['int', 'bool', 'text'].includes(item.kind)),
  };
}

export function formatUsdFromCents(cents) {
  const value = Number(cents);
  if (!Number.isFinite(value)) {
    return '';
  }
  return (value / 100).toFixed(2);
}

export function parseDollarsToCents(value) {
  const parsed = Number.parseFloat(String(value).replace(/[^0-9.]/g, ''));
  if (!Number.isFinite(parsed)) {
    return 0;
  }
  return Math.round(parsed * 100);
}

const UNLIMITED_SENTINELS = new Set(['', '-1', '9999', '99999', 'unlimited', 'null', 'none']);

const INSTRUCTOR_METRIC_ORDER = ['inst.courses', 'inst.students', 'inst.assessments'];
const LEARNER_METRIC_ORDER = [
  'learner.courses',
  'lrn.courses',
  'learner.students',
  'lrn.students',
  'learner.assessments',
  'lrn.assessments',
  'learner.questions',
  'lrn.questions',
];

/** True when an int entitlement value means no cap (null, empty, or high sentinel). */
export function isUnlimitedLimit(value) {
  if (value == null) {
    return true;
  }
  const normalized = String(value).trim().toLowerCase();
  if (!normalized) {
    return true;
  }
  if (UNLIMITED_SENTINELS.has(normalized)) {
    return true;
  }
  const numeric = Number(normalized);
  return Number.isFinite(numeric) && numeric >= 9999;
}

/** Normalizes tier slugs for pricing/checkout comparisons. */
export function normalizeTierSlug(tier) {
  if (!tier) {
    return '';
  }
  return String(tier).trim().toLowerCase();
}

/** Formats dollar amounts from cent values for paywall cards. */
export function formatDollarFromCents(cents) {
  const value = Number(cents);
  if (!Number.isFinite(value)) {
    return '';
  }
  const dollars = value / 100;
  if (dollars % 1 === 0) {
    return `$${dollars.toLocaleString('en-US')}`;
  }
  return `$${dollars.toFixed(2)}`;
}

/** Annual savings vs paying monthly for 12 months (dollars). */
export function computeAnnualSavings(monthlyCents, annualCents) {
  const monthly = Number(monthlyCents) || 0;
  const annual = Number(annualCents) || 0;
  if (monthly <= 0 || annual <= 0) {
    return 0;
  }
  const savingsCents = (monthly * 12) - annual;
  return savingsCents > 0 ? savingsCents / 100 : 0;
}

/** True when any visible tier lists an annual price — drives Monthly/Yearly toggle. */
export function packagesHaveAnnualPricing(packages) {
  return (Array.isArray(packages) ? packages : []).some(
    (pkg) => Number(pkg?.annualCents) > 0,
  );
}

/**
 * Resolves how a paywall tier should display price and which checkout path to use.
 * Enterprise / unpriced tiers → contact sales; starter with $0 → free; else monthly/yearly.
 */
export function resolvePlanPricingDisplay(pkg, options = {}) {
  const billingPeriod = options.billingPeriod === 'yearly' ? 'yearly' : 'monthly';
  const tier = normalizeTierSlug(pkg?.tier);
  const monthlyCents = Number(pkg?.monthlyCents) || 0;
  const annualCents = Number(pkg?.annualCents) || 0;
  const hasListedPrice = monthlyCents > 0 || annualCents > 0;

  if (tier === 'enterprise' || (!hasListedPrice && tier !== 'starter')) {
    return {
      mode: 'contact',
      amountLabel: 'Custom',
      periodLabel: '',
      subLabel: 'Talk to sales',
      annualLabel: null,
      savingsLabel: null,
      checkoutPrice: null,
      billingPeriod: 'monthly',
      ctaPriceLabel: null,
    };
  }

  if (monthlyCents <= 0 && tier === 'starter') {
    return {
      mode: 'free',
      amountLabel: 'Free',
      periodLabel: 'forever',
      subLabel: 'No card required',
      annualLabel: null,
      savingsLabel: null,
      checkoutPrice: 0,
      billingPeriod: 'monthly',
      ctaPriceLabel: 'Free',
    };
  }

  const monthly = monthlyCents / 100;
  const amountLabel = monthly % 1 === 0
    ? `$${monthly.toLocaleString('en-US')}`
    : `$${monthly.toFixed(2)}`;
  const annualDollars = annualCents / 100;
  const savings = computeAnnualSavings(monthlyCents, annualCents);
  const useYearlyCheckout = billingPeriod === 'yearly' && annualCents > 0;
  const annualLabel = annualCents > 0
    ? `${formatDollarFromCents(annualCents)} billed yearly`
    : null;
  const savingsLabel = savings > 0
    ? `save $${savings % 1 === 0 ? savings.toLocaleString('en-US') : savings.toFixed(2)}`
    : null;

  return {
    mode: 'paid',
    amountLabel,
    periodLabel: '/ mo',
    subLabel: null,
    annualLabel: billingPeriod === 'yearly' ? annualLabel : null,
    savingsLabel: billingPeriod === 'yearly' ? savingsLabel : null,
    checkoutPrice: useYearlyCheckout ? annualDollars : monthly,
    billingPeriod: useYearlyCheckout ? 'yearly' : 'monthly',
    ctaPriceLabel: useYearlyCheckout
      ? `${formatDollarFromCents(annualCents)}/yr`
      : `${amountLabel}/mo`,
  };
}

/**
 * Feature rows for upgrade/reactivation cards — meters first, then enabled bool features.
 */
export function buildPaywallFeatureRows(entitlements, planKind = 'instructor', maxFeatures = 5) {
  const list = Array.isArray(entitlements) ? entitlements : [];
  if (!list.length) {
    return [];
  }

  const metrics = extractUsageMetrics(list, planKind);
  const { enabled } = extractPlanFeatures(list);
  const rows = [];

  metrics.forEach((metric) => {
    rows.push({
      key: metric.entKey,
      label: metric.label,
      value: metric.display,
      on: true,
      kind: 'metric',
    });
  });

  const visibleFeatures = enabled.slice(0, maxFeatures);
  visibleFeatures.forEach((feature) => {
    rows.push({
      key: feature.entKey,
      label: feature.label,
      on: true,
      kind: 'feature',
    });
  });

  const remaining = enabled.length - visibleFeatures.length;
  if (remaining > 0) {
    rows.push({
      key: 'features-more',
      label: `${remaining} more feature${remaining === 1 ? '' : 's'} included`,
      on: true,
      kind: 'more',
    });
  }

  return rows;
}

/** Subtitle copy beneath the plan name on paywall cards. */
export function getPaywallPlanBlurb(pkg, planKind = 'instructor') {
  const pricing = resolvePlanPricingDisplay(pkg);
  if (pricing.mode === 'contact') {
    return 'Tailored pricing and onboarding for large programs.';
  }

  const metrics = extractUsageMetrics(pkg?.entitlements, planKind);
  const learners = metrics.find((item) => item.entKey === 'inst.students' || item.entKey === 'learner.students');
  if (learners && !learners.unlimited) {
    return `Designed for teams serving up to ${learners.display} learners.`;
  }
  if (pricing.mode === 'free') {
    return 'Explore StudyAK with no card required.';
  }
  return 'More capacity, features, and support for growing programs.';
}

/** Formats an int entitlement for display (∞ or integer). */
export function formatMeterLimit(value) {
  if (isUnlimitedLimit(value)) {
    return '∞';
  }
  const parsed = Number.parseInt(String(value), 10);
  if (!Number.isFinite(parsed)) {
    return '—';
  }
  return parsed.toLocaleString('en-US');
}

function metricSortIndex(entKey, planKind) {
  const order = planKind === 'learner' ? LEARNER_METRIC_ORDER : INSTRUCTOR_METRIC_ORDER;
  const index = order.indexOf(entKey);
  return index >= 0 ? index : 999;
}

/**
 * All int entitlements for a plan row — used in Packages table and dialog summary.
 */
export function extractUsageMetrics(entitlements, planKind = 'instructor') {
  const list = Array.isArray(entitlements) ? entitlements : [];
  return list
    .filter((item) => item.kind === 'int')
    .map((item) => ({
      entKey: item.entKey,
      label: formatEntitlementLabel(item.entKey),
      display: formatMeterLimit(item.value),
      unlimited: isUnlimitedLimit(item.value),
    }))
    .sort((a, b) => {
      const orderDiff = metricSortIndex(a.entKey, planKind) - metricSortIndex(b.entKey, planKind);
      if (orderDiff !== 0) {
        return orderDiff;
      }
      return a.label.localeCompare(b.label);
    });
}

/**
 * Boolean entitlements split into enabled / disabled feature lists.
 */
export function extractPlanFeatures(entitlements) {
  const list = Array.isArray(entitlements) ? entitlements : [];
  const bools = list.filter((item) => item.kind === 'bool');
  const enabled = [];
  const disabled = [];

  bools.forEach((item) => {
    const isOn = String(item.value).toLowerCase() === 'true';
    const feature = {
      entKey: item.entKey,
      label: formatEntitlementLabel(item.entKey),
    };
    if (isOn) {
      enabled.push(feature);
    } else {
      disabled.push(feature);
    }
  });

  enabled.sort((a, b) => a.label.localeCompare(b.label));
  disabled.sort((a, b) => a.label.localeCompare(b.label));
  return {
    enabled,
    disabled,
    disabledCount: disabled.length,
    total: bools.length,
  };
}
